/* eslint-disable react/jsx-props-no-spreading */

import { useEffect, useState } from "react";
import {
  CheckCircleIcon,
  MessageIcon,
  RightArrowIcon,
} from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import SupportDrawer from "@/components/atoms/drawer/support-drawer";
import Expander from "@/components/atoms/expander/expander-component";
import withMedicalFlow from "@/components/with-medical-flow-v2";
import { PRODUCT } from "@/constants";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { usePartnerMessage } from "@/hooks/use-partner-message";
import { showMessenger } from "@/utils/cxone.utils";
import { restructureObject } from "@/utils/magnolia/magnolia.utils";
import { renderSupportSection } from "@/utils/render-support-section.utils";

import Styles from "./question-detail-section.module.css";
import { Agent } from "../cta-button/cta-buttons";

type QuestionDetailSectionProps = {
  rightContent?: object | null;
  isdesktop?: boolean;
  supportDrawer?: object | null;
  loadMoreButtonText?: string;
  loadMoreButtonEnable?: boolean;
  styleClass?: string;
  supportImage?: string;
  medicalConfirmationPage?: boolean;
  showCompleted?: boolean;
  newMedicalFlowEnabled?: boolean;
  isUndiagnosedQuestion?: boolean;
  chatButtonLabel?: string;
};

function QuestionDetailSection({
  rightContent,
  supportDrawer,
  loadMoreButtonText,
  loadMoreButtonEnable,
  styleClass,
  supportImage,
  medicalConfirmationPage,
  showCompleted = false,
  newMedicalFlowEnabled,
  isUndiagnosedQuestion,
  chatButtonLabel,
}: Readonly<QuestionDetailSectionProps>) {
  const restructuredObj = restructureObject(rightContent);
  const [open, setOpen] = useState(false);
  const onClickLoadMore = () => {
    setOpen(!open);
  };
  const [arrowIconColor, setArrowIconColor] = useState<string>("white");
  const isLargeDesktop = useMediaQuery("(min-width: 900px)");
  const isTablet = useMediaQuery("(min-width: 600px)");

  const brand: "staysure" | "avanti" =
    (PRODUCT as "staysure" | "avanti") ?? "staysure";

  // Use partner message hook for partner-specific title resolution
  const { getPartnerTitle } = usePartnerMessage();
  const { wizard, current } = useWizard();
  const expandFirstSupportDrawerItem =
    current.uiElementControls?.expandFirstSupportDrawerItem;
  const { productSpecialFeatures } = wizard;
  const showNewChatButton = productSpecialFeatures?.showNewChatButton;

  // Resolve partner-specific title if available
  if (restructuredObj) {
    const resolvedTitle = getPartnerTitle(
      restructuredObj.title || "",
      restructuredObj.child
    );
    restructuredObj.title = resolvedTitle;
  }

  useEffect(() => {
    if (medicalConfirmationPage) {
      const colorMap = {
        avanti: "#005A5E",
        staysure: "#306582",
      };

      setArrowIconColor(colorMap[brand] || "white");
    } else {
      setArrowIconColor("white");
    }
  }, [brand, medicalConfirmationPage]);

  const getBgStyles = () => {
    if (!medicalConfirmationPage) {
      return {};
    }

    let height;
    if (isLargeDesktop) {
      height = "100%";
    } else if (isTablet) {
      height = "650px";
    } else {
      height = "500px";
    }

    const justifyContent = isLargeDesktop ? "center" : "flex-start";

    return {
      backgroundImage: `url(${supportImage})`,
      backgroundRepeat: "no-repeat",
      width: "100%",
      height,
      justifyContent,
      backgroundPosition: "center bottom",
      backgroundSize: "cover ",
    };
  };
  return (
    <div
      data-testid="question-details-test-id"
      id="question-details-section-id"
      className={` flex flex-col items-center h-full
      lg:min-h-[632px] md:pt-spacing-xxl md:pb-spacing-xxl pt-spacing-l pb-spacing-m`}
      style={{ ...getBgStyles() }}
    >
      {/* Centered content wrapper for support section and load more button */}
      <div className="flex-1 flex flex-col md:flex-col justify-center items-center w-full order-2 md:order-1">
        <div className="w-full">
          {newMedicalFlowEnabled && showCompleted && (
            <div className="mb-spacing-m flex justify-center">
              <CheckCircleIcon fill="transparent" size="XL" />
            </div>
          )}
          {renderSupportSection(
            restructuredObj,
            styleClass,
            medicalConfirmationPage,
            isUndiagnosedQuestion
          )}
        </div>

        {loadMoreButtonEnable && loadMoreButtonText ? (
          <div className="load-more-wrapper xl:px-spacing-xxxl lg:px-spacing-m md: tablet:px-spacing-xxxl px-spacing-l mt-spacing-m md:mt-spacing-xs">
            <div className="flex flex-row items-center sm:mt-spacing-xs load-more-inner-wrapper">
              <div
                className={` flex flex-row items-center cursor-pointer ${
                  medicalConfirmationPage ? "px-spacing-xl md:p-0" : ""
                } `}
              >
                <LabelButtonWrapper
                  data-testid="loadmoreButton-automate-test-id"
                  id="loadmoreButton-automate-test-id"
                  label={loadMoreButtonText || "Read more on this question"}
                  hierarchy="tertiary"
                  className={`${Styles.focusReadMoreLabel} ${
                    medicalConfirmationPage
                      ? Styles.expand_btnMDFlow
                      : `${Styles.expand_btnQ}`
                  }
                  lg:hover:text-dark-surface-accent-2-primary !p-0 font-semibold text-xs`}
                  type="button"
                  onClick={() => onClickLoadMore()}
                />
                <RightArrowIcon stroke={arrowIconColor} size="M" />
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <SupportDrawer setOpen={setOpen} isOpen={open}>
        {supportDrawer &&
          Object.entries(supportDrawer).map(([key, expanderData], index) => {
            if (
              expanderData["mgnl:template"] === "quote:components/atom/expander"
            ) {
              return (
                <Expander
                  key={key}
                  {...expanderData}
                  defaultExpanded={expandFirstSupportDrawerItem && index === 0}
                />
              );
            }
            return null;
          })}
      </SupportDrawer>

      {/* Agent button at the bottom */}
      {showNewChatButton && (
        <div
          className={`w-full flex justify-center order-1 md:order-3 mb-spacing-m md:mb-0 md:mt-spacing-m lg:mt-0 mt-0 ${Styles.agentButton}`}
        >
          <Agent
            label={chatButtonLabel ?? "Not sure? Chat with us"}
            hierarchy="secondary"
            className="w-[275px] md:w-[300px] h-[56px] px-spacing-l py-spacing-s [&:hover_path]:stroke-interactive-secondary-default"
            rightIcon={
              <span style={{ marginLeft: "4px" }}>
                <MessageIcon stroke="white" fill="white" />
              </span>
            }
            isRightIcon
            semanticColor="Dark Background"
            onClick={() => {
              if (typeof window !== "undefined") {
                showMessenger();
              }
            }}
          />
        </div>
      )}
    </div>
  );
}

export default withMedicalFlow(QuestionDetailSection);
